import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Wand2, History, Receipt, CreditCard, LifeBuoy, Settings,
  LogOut, Sparkles, Coins, Users, Wrench, BadgePercent, FileText, ScrollText,
  HelpCircle, Mail, ChevronRight, Plug, Send, BookPlus, Layers, ShieldCheck, IndianRupee, Search
} from "lucide-react";

function CreditBadge() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Link to="/buy-credits" data-testid="sidebar-buy-credits" className="block mt-4 px-3 py-3 rounded-xl border border-white/10 bg-gradient-to-br from-purple-600/20 to-blue-600/15 hover:border-purple-400/40 transition">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Credit Balance</div>
      <div className="flex items-center justify-between mt-1">
        <div className="bx-heading text-xl font-extrabold" data-testid="sidebar-credit-balance">{user.credits.toLocaleString()}</div>
        <Coins size={18} className="text-purple-300" />
      </div>
      <div className="text-[11px] text-slate-300 mt-2 flex items-center gap-1">
        Buy more <ChevronRight size={12} />
      </div>
    </Link>
  );
}

function Sidebar({ items, title, sub }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <aside className="w-64 shrink-0 border-r border-white/10 bg-[#05050A] flex flex-col min-h-screen sticky top-0">
      <Link to="/" className="px-5 pt-6 pb-4 flex items-center gap-2">
        <span className="bx-glow-icon" style={{ width: 36, height: 36, borderRadius: 10 }}>
          <Sparkles size={18} />
        </span>
        <div className="leading-tight">
          <div className="bx-heading font-extrabold text-lg">BINAREX<span className="text-purple-400">.CO</span></div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">{sub || "AI Powered Solutions"}</div>
        </div>
      </Link>
      <div className="px-3 pb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 px-3 mb-1">{title}</div>
        <nav className="flex flex-col gap-0.5">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`side-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-[0_4px_18px_-6px_rgba(139,92,246,0.6)]" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="px-3 mt-auto pb-5">
        <CreditBadge />
        <button
          onClick={() => { logout(); nav("/"); }}
          data-testid="sidebar-logout"
          className="mt-3 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white border border-white/10"
        >
          <LogOut size={16} /> Logout
        </button>
      </div>
    </aside>
  );
}

export function UserLayout({ children }) {
  const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/dashboard/tools", label: "AI Tools", icon: Wand2 },
    { to: "/dashboard/history", label: "History", icon: History },
    { to: "/dashboard/credits", label: "Credit History", icon: Coins },
    { to: "/dashboard/invoices", label: "Invoices", icon: Receipt },
    { to: "/buy-credits", label: "Buy Credits", icon: CreditCard },
    { to: "/dashboard/support", label: "Support", icon: LifeBuoy },
    { to: "/dashboard/affiliate", label: "Affiliate", icon: BadgePercent },
    { to: "/dashboard/settings", label: "Account", icon: Settings },
  ];
  return (
    <div className="min-h-screen flex bg-[#05050A] text-slate-100">
      <Sidebar items={items} title="User Panel" sub="AI Powered Solutions" />
      <main className="flex-1 bg-[#06060D] min-w-0 bx-scrollbar">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-7">{children}</div>
      </main>
    </div>
  );
}

export function AdminLayout({ children }) {
  const items = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/tools", label: "AI Tools", icon: Wrench },
    { to: "/admin/categories", label: "Categories", icon: Layers },
    { to: "/admin/plans", label: "Plans", icon: BadgePercent },
    { to: "/admin/gateways", label: "Payment Gateways", icon: Plug },
    { to: "/admin/invoices", label: "Invoices", icon: Receipt },
    { to: "/admin/transactions", label: "Transactions", icon: Coins },
    { to: "/admin/affiliate", label: "Affiliate", icon: BadgePercent },
    { to: "/admin/blog", label: "Blog", icon: FileText },
    { to: "/admin/blog/bulk", label: "Bulk Blog AI", icon: BookPlus },
    { to: "/admin/pages", label: "Pages", icon: ScrollText },
    { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
    { to: "/admin/smtp", label: "SMTP / Email", icon: Send },
    { to: "/admin/settings", label: "Website Settings", icon: Settings },
    { to: "/admin/tickets", label: "Support", icon: LifeBuoy },
    { to: "/admin/messages", label: "Messages", icon: Mail },
    { to: "/admin/audit-log", label: "Audit Log", icon: ShieldCheck },
    { to: "/admin/upi", label: "UPI Pay Link", icon: IndianRupee },
    { to: "/admin/aggregators", label: "Aggregators", icon: Plug },
    { to: "/admin/email-templates", label: "Email Templates", icon: Mail },
    { to: "/admin/seo", label: "SEO Editor", icon: Search },
  ];
  return (
    <div className="min-h-screen flex bg-[#05050A] text-slate-100">
      <Sidebar items={items} title="Admin Panel" sub="Super Administrator" />
      <main className="flex-1 bg-[#06060D] min-w-0 bx-scrollbar">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-7">{children}</div>
      </main>
    </div>
  );
}
